import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Tooltip,
  ButtonBase
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PATH_DASHBOARD, PATH_ORG } from '../../routes/paths';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import type { RootState } from '../../redux/store';
import { AddTopicDialog } from './AddTopicDialog';

type CreatePopoverProps = {
  collapsed?: boolean;
};

export default function CreatePopover({ collapsed = false }: CreatePopoverProps) {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const activeOrgGuid = useSelector((s: RootState) => s.organizationContext.activeOrgGuid);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [addTopicOpen, setAddTopicOpen] = useState(false);
  const open = Boolean(anchor);

  const handleClose = () => setAnchor(null);

  return (
    <>
      <Tooltip title={collapsed ? translate('overview.create') : ''} placement="right">
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={translate('overview.create')}
          aria-haspopup="menu"
          aria-expanded={open}
          sx={{
            width: collapsed ? 40 : '100%',
            minWidth: collapsed ? 40 : 0,
            height: collapsed ? 40 : 36,
            mx: collapsed ? 'auto' : 0,
            px: collapsed ? 0 : 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 1,
            borderRadius: 1,
            color: 'text.primary',
            backgroundColor: collapsed ? 'transparent' : 'background.neutral',
            '&:hover': {
              backgroundColor: 'action.hover'
            },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2
            }
          }}
        >
          <Box
            sx={{
              width: 28,
              minWidth: 28,
              height: 24,
              display: 'grid',
              placeItems: 'center',
              color: 'text.secondary'
            }}
          >
            <AddIcon sx={{ fontSize: 19 }} />
          </Box>
          {!collapsed && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                {translate('overview.create')}
              </Typography>
              <KeyboardArrowDownIcon
                sx={{
                  ml: 'auto',
                  fontSize: 17,
                  color: 'text.secondary',
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: (theme) =>
                    theme.transitions.create('transform', {
                      duration: theme.transitions.duration.shortest
                    })
                }}
              />
            </>
          )}
        </ButtonBase>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
      >
        {!activeOrgGuid && (
          <>
            <MenuItem
              onClick={() => {
                handleClose();
                setAddTopicOpen(true);
              }}
            >
              <ListItemIcon>
                <ArticleOutlinedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={translate('overview.create_topic_label')}
                secondary={translate('overview.add_topic_subtitle')}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    sx: { fontWeight: (theme) => theme.typography.fontWeightMedium }
                  },
                  secondary: { variant: 'caption' }
                }}
              />
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />
          </>
        )}

        <MenuItem
          onClick={() => {
            handleClose();
            navigate(activeOrgGuid ? PATH_ORG.pathwaysCreate : PATH_DASHBOARD.pathway.new);
          }}
        >
          <ListItemIcon>
            <AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary={translate('overview.create_pathway_label')}
            secondary={translate('overview.new_pathway_subtitle')}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'caption' } }}
          />
        </MenuItem>
      </Menu>
      <AddTopicDialog open={addTopicOpen} onClose={() => setAddTopicOpen(false)} />
    </>
  );
}
